<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import CompanyManAppLayout from '@/components/layout/CompanyManAppLayout.vue'
import CompanyManCard from '@/components/dashboard/CompanyManCard.vue'
import CompanyManMetric from '@/components/dashboard/CompanyManMetric.vue'
import CompanyManRow from '@/components/dashboard/CompanyManRow.vue'
import CompanyManAction from '@/components/dashboard/CompanyManAction.vue'

type DashboardPayload = {
  company_man?: Record<string, any>
  stats?: Record<string, any>
  active_locations?: Array<Record<string, any>>
  tickets?: Array<Record<string, any>>
  reports?: Array<Record<string, any>>
  tanks?: Array<Record<string, any>>
  supervisors?: Array<Record<string, any>>
  alerts?: Array<Record<string, any>>
  safety?: Array<Record<string, any>>
  activity?: Array<Record<string, any>>
}

const router = useRouter()
const loading = ref(true)
const refreshing = ref(false)
const error = ref('')
const updatedAt = ref(new Date())

const dashboard = ref<DashboardPayload>({
  company_man: {
    name: 'Company Man',
    operator: 'Operator',
    region: 'North Dakota',
  },
  stats: {
    active_locations: 0,
    tickets_pending: 0,
    reports_due: 0,
    tank_alerts: 0,
    crew_on_site: 0,
    safety_alerts: 0,
  },
  active_locations: [],
  tickets: [],
  reports: [],
  tanks: [],
  supervisors: [],
  alerts: [],
  safety: [],
  activity: [],
})

const stats = computed(() => dashboard.value.stats || {})
const locations = computed(() => dashboard.value.active_locations || [])
const tickets = computed(() => dashboard.value.tickets || [])
const reports = computed(() => dashboard.value.reports || [])
const tanks = computed(() => dashboard.value.tanks || [])
const supervisors = computed(() => dashboard.value.supervisors || [])
const alerts = computed(() => dashboard.value.alerts || [])
const safety = computed(() => dashboard.value.safety || [])
const activity = computed(() => dashboard.value.activity || [])

const routeMap: Record<string, string> = {
  '/company-man/approvals': '/company-man/field-tickets',
  '/company-man/locations': '/company-man',
  '/company-man/crew': '/company-man',
  '/company-man/safety': '/company-man/safety',
  '/company-man/messages': '/company-man',
}

function go(path: string) { router.push(routeMap[path] || path) }

async function loadDashboard(showSpinner = true) {
  if (showSpinner) loading.value = true
  refreshing.value = !showSpinner
  error.value = ''
  try {
    const token = localStorage.getItem('auth_token')
    const response = await fetch('/api/company-man/dashboard', {
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
    if (!response.ok) throw new Error('Unable to load Company Man dashboard')
    const payload = await response.json()
    const inner = payload.dashboard || {}
    const tanks = (payload.tanks || inner.tanks || []).map((tank: any) => ({
      ...tank,
      fill_pct: tank.fill_pct ?? Math.round(Number(tank.oil_pct || 0) + Number(tank.water_pct || 0)),
    }))
    const locations = payload.active_locations || inner.active_locations || (inner.jobs || []).map((job: any) => ({
      ...job,
      name: job.well_name || job.name,
      crew_count: job.crew_count ?? inner.stats?.crew_on_site ?? 0,
      tank_count: job.tank_count ?? tanks.length,
      alerts: job.alerts ?? inner.stats?.open_alerts ?? 0,
    }))
    dashboard.value = {
      ...dashboard.value,
      ...payload,
      ...inner,
      company_man: {
        ...dashboard.value.company_man,
        ...(payload.company_man || payload.user || payload.profile || inner.profile || {}),
        name: payload.company_man?.name || payload.profile?.name || inner.profile?.name || dashboard.value.company_man?.name,
        operator: payload.company_man?.operator || payload.profile?.operatorCompany || inner.profile?.operatorCompany,
        region: payload.company_man?.region || payload.profile?.serviceCompany || inner.profile?.serviceCompany,
      },
      stats: {
        ...dashboard.value.stats,
        ...(payload.stats || inner.stats || {}),
        active_locations: (payload.stats || inner.stats || {}).active_locations ?? locations.length,
        tickets_pending: (payload.stats || inner.stats || {}).tickets_pending ?? (inner.stats || {}).pending_tickets ?? 0,
        tank_alerts: (payload.stats || inner.stats || {}).tank_alerts ?? (inner.stats || {}).open_alerts ?? 0,
        safety_alerts: (payload.stats || inner.stats || {}).safety_alerts ?? (inner.stats || {}).safety_incidents_today ?? 0,
      },
      active_locations: locations,
      tanks,
      tickets: payload.tickets || inner.tickets || [],
      reports: payload.reports || inner.reports || [],
      alerts: payload.alerts || inner.alerts || [],
      supervisors: payload.supervisors || inner.supervisors || [],
      safety: payload.safety || inner.safety || [],
      activity: payload.activity || inner.activity || [],
    }
  } catch (e: any) {
    error.value = e?.message || 'Company Man dashboard data could not be loaded.'
  } finally {
    loading.value = false
    refreshing.value = false
    updatedAt.value = new Date()
  }
}

onMounted(() => loadDashboard())
</script>

<template>
  <CompanyManAppLayout page-title="Company Man Operations" page-subtitle="Approvals, reports, tanks, and location oversight">
    <div class="mx-auto w-full max-w-[1700px]">

        <section class="mb-5 flex flex-col gap-4 rounded-2xl border border-slate-700/50 bg-slate-800/45 p-4 shadow-xl backdrop-blur-xl xl:flex-row xl:items-center xl:justify-between">
          <div class="flex items-center gap-3">
            <div class="grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-emerald-400 font-black text-slate-950">CM</div>
            <div>
              <div class="flex flex-wrap items-center gap-2">
                <h1 class="text-2xl font-extrabold tracking-tight text-white">Company Man Operations</h1>
                <span class="rounded-full border border-cyan-500/25 bg-cyan-500/10 px-2.5 py-1 text-[11px] font-bold uppercase text-cyan-300">Operator Oversight</span>
              </div>
              <p class="mt-1 text-sm text-slate-400">{{ dashboard.company_man?.name }} · {{ dashboard.company_man?.operator }} · {{ dashboard.company_man?.region }}</p>
            </div>
          </div>
          <div class="flex flex-wrap gap-2">
            <button class="rounded-xl border border-slate-600/50 bg-slate-700/50 px-4 py-2 text-sm font-semibold" @click="loadDashboard(false)">{{ refreshing ? 'Refreshing…' : '↻ Refresh' }}</button>
            <button class="rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 px-4 py-2 text-sm font-extrabold text-slate-950" @click="go('/company-man/approvals')">Review Approvals</button>
          </div>
        </section>

        <div v-if="error" class="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{{ error }}</div>

        <div v-if="loading" class="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
          <div v-for="n in 12" :key="n" class="h-28 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40"></div>
        </div>

        <template v-else>
          <section class="mb-5 grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
            <CompanyManMetric title="Active Locations" :value="stats.active_locations ?? 0" subtitle="Wells under oversight" tone="cyan" icon="location" @click="go('/company-man/locations')" />
            <CompanyManMetric title="Tickets Pending" :value="stats.tickets_pending ?? 0" subtitle="Awaiting approval" tone="purple" icon="ticket" @click="go('/company-man/field-tickets')" />
            <CompanyManMetric title="Reports Due" :value="stats.reports_due ?? 0" subtitle="Supervisor reports" tone="amber" icon="report" @click="go('/company-man/reports')" />
            <CompanyManMetric title="Tank Alerts" :value="stats.tank_alerts ?? 0" subtitle="High / exception" tone="red" icon="tank" @click="go('/company-man/tanks')" />
            <CompanyManMetric title="Crew On Site" :value="stats.crew_on_site ?? 0" subtitle="Across locations" tone="emerald" icon="crew" @click="go('/company-man/crew')" />
            <CompanyManMetric title="Safety Alerts" :value="stats.safety_alerts ?? 0" subtitle="Open safety items" tone="blue" icon="safety" @click="go('/company-man/safety')" />
          </section>

          <section class="mb-5 grid gap-5 xl:grid-cols-12">
            <CompanyManCard class="xl:col-span-6" title="Active Locations" subtitle="Operator-level view of wells and service crews" action-label="All Locations" @action="go('/company-man/locations')">
              <div class="grid gap-3 md:grid-cols-2">
                <div v-for="(loc, i) in locations.slice(0,6)" :key="loc.id || i" class="rounded-xl border border-slate-700/50 bg-slate-900/30 p-4">
                  <div class="flex justify-between gap-3">
                    <div><span class="text-[10px] uppercase tracking-wide text-cyan-300">{{ loc.operator || dashboard.company_man?.operator }}</span><h3 class="mt-1 font-extrabold text-white">{{ loc.well_name || loc.name || `Location ${i+1}` }}</h3></div>
                    <span class="h-fit rounded-full bg-emerald-500/10 px-2 py-1 text-[10px] font-bold text-emerald-300">{{ loc.status || 'Active' }}</span>
                  </div>
                  <p class="mt-2 text-xs text-slate-500">{{ loc.location || '' }}</p>
                  <div class="mt-4 grid grid-cols-3 gap-2 text-center">
                    <div class="rounded-lg bg-slate-950/35 p-2"><span class="block text-[9px] text-slate-500">Crew</span><strong class="text-sm text-white">{{ loc.crew_count ?? 0 }}</strong></div>
                    <div class="rounded-lg bg-slate-950/35 p-2"><span class="block text-[9px] text-slate-500">Tanks</span><strong class="text-sm text-white">{{ loc.tank_count ?? 0 }}</strong></div>
                    <div class="rounded-lg bg-slate-950/35 p-2"><span class="block text-[9px] text-slate-500">Alerts</span><strong class="text-sm" :class="Number(loc.alerts || 0) ? 'text-red-300':'text-emerald-300'">{{ loc.alerts ?? 0 }}</strong></div>
                  </div>
                </div>
                <div v-if="!locations.length" class="col-span-full rounded-xl border border-dashed border-slate-700/60 p-8 text-center text-sm text-slate-500">Assigned locations will appear here.</div>
              </div>
            </CompanyManCard>

            <CompanyManCard class="xl:col-span-3" title="Quick Actions" subtitle="Operator approval and oversight">
              <div class="grid grid-cols-2 gap-3">
                <CompanyManAction label="Approve Tickets" icon="ticket" tone="purple" @click="go('/company-man/field-tickets')" />
                <CompanyManAction label="Daily Reports" icon="report" tone="cyan" @click="go('/company-man/reports')" />
                <CompanyManAction label="Tank Levels" icon="tank" tone="red" @click="go('/company-man/tanks')" />
                <CompanyManAction label="Crew On Site" icon="crew" tone="emerald" @click="go('/company-man/crew')" />
                <CompanyManAction label="Safety" icon="safety" tone="amber" @click="go('/company-man/safety')" />
                <CompanyManAction label="Messages" icon="message" tone="blue" @click="go('/company-man/messages')" />
              </div>
            </CompanyManCard>

            <CompanyManCard class="xl:col-span-3" title="Priority Alerts" subtitle="Exceptions requiring operator attention">
              <div class="space-y-2">
                <CompanyManRow v-for="(item,i) in alerts.slice(0,6)" :key="item.id || i" :title="item.title || 'Alert'" :subtitle="item.message || item.location || ''" :meta="item.time || ''" :tone="item.severity === 'high' ? 'red' : 'amber'" />
                <div v-if="!alerts.length" class="grid min-h-52 place-items-center rounded-xl bg-slate-900/25 text-center"><div><div class="text-2xl">✓</div><p class="mt-2 font-semibold text-white">No priority alerts</p><p class="mt-1 text-xs text-slate-500">Operational exceptions appear here.</p></div></div>
              </div>
            </CompanyManCard>
          </section>

          <section class="mb-5 grid gap-5 xl:grid-cols-12">
            <CompanyManCard class="xl:col-span-4" title="Field Ticket Approvals" subtitle="Approve or reject completed work" action-label="Review All" @action="go('/company-man/field-tickets')">
              <div class="space-y-2">
                <CompanyManRow v-for="(item,i) in tickets.slice(0,6)" :key="item.id || i" :title="item.ticket_number || `Ticket ${i+1}`" :subtitle="[item.company, item.well_name].filter(Boolean).join(' · ')" :meta="item.status || 'Pending'" :tone="item.status === 'approved' ? 'emerald' : item.status === 'rejected' ? 'red' : 'purple'" />
                <div v-if="!tickets.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">No field tickets awaiting approval.</div>
              </div>
            </CompanyManCard>

            <CompanyManCard class="xl:col-span-4" title="Daily Reports" subtitle="Supervisor reports from active locations" action-label="View Reports" @action="go('/company-man/reports')">
              <div class="space-y-2">
                <CompanyManRow v-for="(item,i) in reports.slice(0,6)" :key="item.id || i" :title="item.well_name || item.title || `Report ${i+1}`" :subtitle="[item.supervisor, item.report_time].filter(Boolean).join(' · ')" :meta="item.status || ''" :tone="item.status === 'received' ? 'emerald' : item.status === 'overdue' ? 'red' : 'cyan'" />
                <div v-if="!reports.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">Daily reports will appear here.</div>
              </div>
            </CompanyManCard>

            <CompanyManCard class="xl:col-span-4" title="Supervisor Status" subtitle="Who is running each location">
              <div class="space-y-2">
                <CompanyManRow v-for="(item,i) in supervisors.slice(0,6)" :key="item.id || i" :title="item.name || `Supervisor ${i+1}`" :subtitle="[item.well_name, item.company].filter(Boolean).join(' · ')" :meta="item.status || item.shift || ''" tone="emerald" />
                <div v-if="!supervisors.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">Supervisor assignments will appear here.</div>
              </div>
            </CompanyManCard>
          </section>

          <section class="mb-5 grid gap-5 xl:grid-cols-12">
            <CompanyManCard class="xl:col-span-7" title="Tank Overview" subtitle="Oil and water levels across assigned locations" action-label="Full Tank View" @action="go('/company-man/tanks')">
              <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div v-for="(tank,i) in tanks.slice(0,6)" :key="tank.id || i" class="rounded-xl border border-slate-700/50 bg-slate-900/30 p-4">
                  <div class="flex justify-between gap-2"><strong class="text-sm text-white">{{ tank.name || tank.tank_name || `Tank ${i+1}` }}</strong><span class="text-xs font-bold" :class="Number(tank.fill_pct || 0) >= 85 ? 'text-red-300':'text-cyan-300'">{{ tank.fill_pct ?? 0 }}%</span></div>
                  <p class="mt-1 truncate text-[10px] text-slate-500">{{ tank.well_name || tank.location || '' }}</p>
                  <div class="mt-3 h-2 overflow-hidden rounded-full bg-slate-700/50"><div class="h-full rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400" :style="{width:`${Math.min(100, Number(tank.fill_pct || 0))}%`}"></div></div>
                  <div class="mt-3 flex justify-between text-[10px] text-slate-500"><span>Oil {{ tank.oil_bbl ?? 0 }} bbl</span><span>Water {{ tank.water_bbl ?? 0 }} bbl</span></div>
                </div>
                <div v-if="!tanks.length" class="col-span-full rounded-xl border border-dashed border-slate-700/60 p-6 text-center text-sm text-slate-500">Synchronized tank readings will appear here.</div>
              </div>
            </CompanyManCard>

            <CompanyManCard class="xl:col-span-5" title="Safety & Compliance" subtitle="Open incidents, JSA and location concerns" action-label="Safety Center" @action="go('/company-man/safety')">
              <div class="space-y-2">
                <CompanyManRow v-for="(item,i) in safety.slice(0,6)" :key="item.id || i" :title="item.title || item.type || 'Safety Item'" :subtitle="[item.well_name, item.description].filter(Boolean).join(' · ')" :meta="item.status || ''" :tone="item.severity === 'high' ? 'red' : item.status === 'closed' ? 'emerald' : 'amber'" />
                <div v-if="!safety.length" class="rounded-xl border border-dashed border-slate-700/60 p-6 text-center text-sm text-slate-500">No open safety items.</div>
              </div>
            </CompanyManCard>
          </section>

          <CompanyManCard title="Operator Activity Feed" subtitle="Reports, approvals, tank updates, crew and safety activity">
            <div class="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              <CompanyManRow v-for="(item,i) in activity.slice(0,9)" :key="item.id || i" :title="item.title || item.description || 'Activity'" :subtitle="item.description && item.title ? item.description : item.location || ''" :meta="item.time || item.created_at || ''" :tone="item.status === 'danger' ? 'red' : item.status === 'warning' ? 'amber' : item.status === 'success' ? 'emerald' : 'cyan'" />
              <div v-if="!activity.length" class="col-span-full rounded-xl border border-dashed border-slate-700/60 p-8 text-center text-sm text-slate-500">Location activity will appear here as OilBuddy receives field updates.</div>
            </div>
          </CompanyManCard>
        </template>
    </div>
  </CompanyManAppLayout>
</template>
